import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment';
import Api from "services/Api";
import store from 'store/index';
import {validDate} from 'components/form/validator/validations';
import {carePeriodTemplate, careTemplate, customHeadsOfLossTemplate} from 'store/modules/shared/templates';
import Validator from 'services/Validator';
import {previousValid} from 'store/generators/step';

/**
 * Mutation Types
 *
 * @type {Object}
 */
const mutationTypes = {
    SET: 'SET',
    RESET: 'RESET',
    UPDATE_HEADS_OF_LOSS_VALUE: 'UPDATE_HEADS_OF_LOSS_VALUE',
    UPDATE_CARE_VALUE: 'UPDATE_CARE_VALUE',
    ADD_CUSTOM_HEAD_OF_LOSS: 'ADD_CUSTOM_HEAD_OF_LOSS',
    UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE: 'UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE',
    DELETE_CUSTOM_HEAD_OF_LOSS: 'DELETE_CUSTOM_HEAD_OF_LOSS',
    ADD_CARE_PERIOD: 'ADD_CARE_PERIOD',
    DELETE_CARE_PERIOD: 'DELETE_CARE_PERIOD',
    UPDATE_CARE_PERIOD_VALUE: 'UPDATE_CARE_PERIOD_VALUE',
    UPDATE_INTEREST_VALUE: 'UPDATE_INTEREST_VALUE',
    UPDATE_FROM_SERVER: 'UPDATE_FROM_SERVER',
    CLEAR_INTEREST: 'CLEAR_INTEREST'
};

const numberVal = value => parseFloat(value || 0);

/**
 * Calculate the total Heads of Loss
 *
 * @type {number}
 */
const calcHeadsOfLossTotal = () => {
    let customHeadsOfLossTotal = state.customHeadsOfLoss.reduce((total, customHeadsOfLoss) => total + numberVal(customHeadsOfLoss.value), 0);
    let customHeadsOfLossTotalWithInterest = state.customHeadsOfLoss.reduce((total, customHeadsOfLoss) => total + numberVal(customHeadsOfLoss.withInterest), 0);
    let total = [
        state.generalDamages,
        state.generalDamagesWithInterest,
        state.pastCostOfCare,
        state.pastCostOfCareWithInterest,
        state.pastCostOfTravel,
        state.pastCostOfTravelWithInterest,
        state.pastLossOfEarnings,
        state.pastLossOfEarningsWithInterest,
        state.futureCostOfCare,
        state.futureCostOfCareWithInterest,
        state.pastDependencyOnServices,
        state.pastDependencyOnServicesWithInterest,
        state.futureDependencyOnServices,
        state.misc,
        state.miscWithInterest,
        customHeadsOfLossTotal,
        customHeadsOfLossTotalWithInterest,
    ].reduce((total, value) => total + parseFloat(value, 10), 0);

    return numberVal(total).toFixed(2);
};

/**
 * Calculate the total Heads of Loss without interest
 *
 * @type {number}
 */
const calcHeadsOfLossTotalMinusInterest = () => {
    let customHeadsOfLossTotal = state.customHeadsOfLoss.reduce((total, customHeadsOfLoss) => total + numberVal(customHeadsOfLoss.value), 0);

    let total = [
        state.generalDamages,
        state.pastCostOfCare,
        state.pastCostOfTravel,
        state.pastLossOfEarnings,
        state.futureCostOfCare,
        state.pastDependencyOnServices,
        state.futureDependencyOnServices,
        state.misc,
        customHeadsOfLossTotal,
    ].reduce((total, value) => total + parseFloat(value, 10), 0);

    return numberVal(total).toFixed(2);
};

/**
 * Return the payload required for the interest calculation modals
 *
 * @type {object}
 */
const modalPayload = () => {
    return {
        interimPayments: store.getters['lost/interimPayments/interimPayments'],
        assumedSettlement: store.getters['lost/claimant/getClaimantInfoValue']('dateOfAssumedSettlement'),
        serviceOfProceedings: store.getters['lost/claimant/getClaimantInfoValue']('dateOfServiceProceedings'),
        onsetOfSymptoms: store.getters['lost/claimant/getClaimantValue']('onsetOfSymptoms'),
        assumedLifeExpectancy: store.getters['lost/claimant/getClaimantValue']('assumedLifeExpectancy'),
        deductibles: store.getters['lost/cruBenefits/all'],
        headsOfLoss: state,
        headOfLoss: {}
    }
};

/**
 * Initial State
 *
 * @type {Object}
 */
const state = {
    generalDamages: 0,
    pastCostOfTravel: 0,
    pastLossOfEarnings: 0,
    pastCostOfCare: 0,
    futureCostOfCare: 0,
    pastDependencyOnServices: 0,
    futureDependencyOnServices: 0,
    misc: 0,
    generalDamagesWithInterest: 0,
    pastCostOfTravelWithInterest: 0,
    pastCostOfCareWithInterest: 0,
    futureCostOfCareWithInterest: 0,
    pastDependencyOnServicesWithInterest: 0,
    miscWithInterest: 0,
    pastLossOfEarningsWithInterest: 0,
    generalDamagesInterest: 2.00,
    pastCostOfTravelInterest: 0.5,
    miscInterest: 0.5,
    pastCostOfCareInterest: 0.5,
    futureCostOfCareInterest: 0.5,
    pastLossOfEarningsInterest: 0.5,
    pastDependencyOnServicesInterest: 0.5,
    customHeadsOfLoss: [],
    care: careTemplate(),
    interestType: 'today',
    customInterestDate: '',
};

/**
 * Validation Rules.
 * 
 * @type {Object}
 */
const rules = {
};

/**
 * Getters.
 *
 * @type {Object}
 */
const getters = {
    getAll: state => state,
    getModalPayload: state => modalPayload(),
    getHeadsOfLossValue: state => key => state[key],
    getHeadsOfLossTotal: state => calcHeadsOfLossTotal(),
    getCareValue: state => key => state.care[key],
    getAllCareValue: state => ( state.care.past.totalValue + state.care.future.totalValue ),
    getCustomHeadOfLoss: state => index => state.customHeadsOfLoss[index],
    getAllCustomHeadOfLoss: state => state.customHeadsOfLoss,
    getAllCustomHeadOfLossTotal: state => state.customHeadsOfLoss.reduce((total, customHeadsOfLoss) => total + numberVal(customHeadsOfLoss.value), 0),
    getAllCustomHeadOfLossInterestTotal: state => state.customHeadsOfLoss.reduce((total, customHeadsOfLoss) => total + numberVal(customHeadsOfLoss.interestAccrued), 0),
    getHeadsOfLossTotalMinusInterest: state => calcHeadsOfLossTotalMinusInterest(),
    allHeadsOfLossValid: state => state.customHeadsOfLoss.reduce((valid, headOfLoss) =>  {
        return !valid ? valid : headOfLoss.name !== '' && headOfLoss.value > 0
    }, true),
    getCarePeriod: state => (type, index)  => state.care[type].periods[index],
    allCarePeriodsValid: state => type => state.care[type].periods.reduce((valid, period) => {
        if (!valid) { return valid; }
        return moment(period.periodStart, 'DD/MM/YYYY').isValid() &&
            moment(period.periodEnd, 'DD/MM/YYYY').isValid() &&
            period.hoursPerDay > 0 ;
    }, true),
    interestDate: (state) => {
        switch (true) {
            case state.interestType === 'today':
                return moment().format('DD/MM/YYYY');
                break;
            case state.interestType === 'assumed_settlement':
                return store.getters['lost/claimant/getClaimantInfoValue']('dateOfAssumedSettlement');
                break;
            case state.interestType === 'custom':
                return state.customInterestDate;
                break
        }
    }
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     *
     * @param getters
     * @param dispatch
     * @param payload
     */
    save({getters, dispatch}, payload) {
        // We might change this to something which
        // is a bit cleaner...
        payload.data = state;

        Api.claims.save(payload).then((response) => {
            return response;
        });
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * @param dispatch
     * @param action
     * @param commit
     * @param step
     */
    load({ dispatch, action, commit }, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;

        const care = data.care;
        const customHeadOfLoss = data.customHeadOfLoss;

        commit(mutationTypes.SET, { key: 'care', value: care });
        commit(mutationTypes.SET, { key: 'customHeadOfLoss', value: customHeadOfLoss });
        commit(mutationTypes.UPDATE_FROM_SERVER, { data });
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     *
     * @param commit
     * @param dispatch
     */
    process({ commit, dispatch }) {
        // This is not currently used.
    },

    /**
     * Validates the step once any processing is complete.
     *
     * @param commit
     * @param dispatch
     */
    validate({ commit, dispatch, rootState }) {
        store.dispatch('lost/step/setStepNumberValid', {
            stepNo: 4,
            valid:  previousValid(rootState, 4) && (new Validator(state, rules)).validate(),
        });
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({ commit }) {
        store.dispatch('lost/step/setStepNumberValid', {
            stepNo: 4,
            valid: false,
        });
        
        commit(mutationTypes.RESET);
    },

    /**
     * Update a piece of heads of loss data.
     *
     * @param commit
     * @param key
     * @param value
     */
    setHeadsOfLossValue({ commit }, { key, value }) {
        commit(mutationTypes.UPDATE_HEADS_OF_LOSS_VALUE, { key, value });
    },

    /**
     * Update a piece of care data.
     *
     * @param commit
     * @param key
     * @param value
     * @param type
     */
    setCareValue({ commit }, { key, value, type }) {
        commit(mutationTypes.UPDATE_CARE_VALUE, { key, value, type });

        if (type) {
            commit(mutationTypes.UPDATE_INTEREST_VALUE, { key: type+'CostOfCare', value: numberVal(value) });
        }
    },

    /**
     * Add custom heads of loss
     *
     * @param commit
     */
    addCustomHeadOfLoss({commit}) {
        commit(mutationTypes.ADD_CUSTOM_HEAD_OF_LOSS);
    },

    /**
     * Update custom heads of loss
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param key
     * @param value
     * @param index
     */
    setCustomHeadOfLossValue({ commit, state, dispatch }, { key, value, index }) {
        if (state.customHeadsOfLoss[index][key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE, { key, value, index });
        return value;
    },

    /**
     * Delete custom heads of loss
     *
     * @param commit
     * @param index
     */
    deleteCustomHeadOfLoss({commit}, index) {
        commit(mutationTypes.DELETE_CUSTOM_HEAD_OF_LOSS, index);
    },

    /**
     * Auto calculate care
     *
     * @param commit
     * @param dispatch
     * @param value
     */
    autoCalculateCare: _.debounce(({ commit, dispatch }, value) => {
        commit(mutationTypes.UPDATE_CARE_VALUE, { key: 'periods', value: [] });
        commit(mutationTypes.UPDATE_CARE_VALUE, { key: 'ownCareFigure', value: 0 });
        return Api.calculators.monetary.autoCalculateCare(false)
            .then(care => dispatch('setCareValue', {key: 'totalValue', value: care.value, type: 'past'}));
    }, 500),

    /**
     * Add care period
     *
     * @param commit
     * @param type
     */
    addCarePeriod({commit}, type) {
        commit(mutationTypes.ADD_CARE_PERIOD, type);
    },

    /**
     * Update care period value
     *
     * @param commit
     * @param state
     * @param key
     * @param value
     * @param index
     * @param type
     */
    updateCarePeriodValue({commit, state}, {key, value, index, type}) {
        // Prevent there being more than 24 hours in a day. Min/max attributes are buggy.
        if (key === 'hoursPerDay' && value > 24) {
            value = 24;
        }

        if (state.care[type].periods[index][key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_CARE_PERIOD_VALUE, { key, value, index, type });
        return value;
    },

    /**
     * Update care period
     *
     * @param dispatch
     * @param commit
     * @param care
     * @param index
     * @param type
     */
    updateCarePeriod({ dispatch, commit }, { care, index, type }) {
        Object.keys(care[0]).forEach((key) => {
            const value = care[0][key];
            commit(mutationTypes.UPDATE_CARE_PERIOD_VALUE, { key, value, index, type });
        });
        dispatch('updateCareTotal', type);
    },

    /**
     * Delete care period
     *
     * @param commit
     * @param dispatch
     * @param state
     * @param index
     * @param type
     */
    deleteCarePeriod({commit, dispatch, state}, {index, type}) {
        commit(mutationTypes.DELETE_CARE_PERIOD, {index, type});
        if (state.care[type].periods.length === 0) {
            dispatch('addCarePeriod', type);
        }
        return dispatch('updateCareTotal', type);
    },

    /**
     * Update care total value
     *
     * @param commit
     * @param state
     * @param type
     */
    updateCareTotal({ commit, state }, type) {
        const value = state.care[type].periods.reduce((total, period) => total + numberVal(period.total), 0);
        commit(mutationTypes.UPDATE_CARE_VALUE, { key: 'totalValue', value: numberVal(value), type });
        commit(mutationTypes.UPDATE_INTEREST_VALUE, { key: type+'CostOfCare', value: numberVal(value) });
    },

    /**
     * Calculate the care period total
     *
     * @param dispatch
     * @param state
     * @param index
     * @param type
     */
    calcCareTotalForPeriod: _.debounce(({ dispatch, state }, {index, type}) => {
        if (validDate(state.care[type].periods[index].periodStart) && validDate(state.care[type].periods[index].periodEnd) && state.care[type].periods[index].hoursPerDay > 0) {
            return Api.calculators.monetary.detailedCare([state.care[type].periods[index]], state.care[type].periods[index].gratuitousCare)
                .then(care => dispatch('updateCarePeriod', { care, index, type }));
        }
    }, 500),

    /**
     * Calculate all the interest
     *
     * @param dispatch
     * @param state
     */
    calcAllInterest({dispatch, state}) {
        if (state.interestType === 'none' ) { dispatch('clearInterest'); return; }
        if (state.interestType === 'custom' && state.customInterestDate === '' ) { return; }

        let value = store.getters['lost/claimant/getAllPastLossOfIncomeTotal'];
        dispatch('setHeadsOfLossValue', {key: 'pastLossOfEarnings', value});

        return Api.calculators.monetary.interest(
            store.getters['lost/claimant/getClaimantInfoValue']('proceduralStatus'),
            store.getters['lost/claimant/getClaimantInfoValue']('dateOfAssumedSettlement'),
            store.getters['lost/claimant/getClaimantInfoValue']('dateOfServiceProceedings'),
            store.getters['lost/claimant/getClaimantValue']('onsetOfSymptoms'),
            store.getters['lost/claimant/getClaimantValue']('assumedLifeExpectancy'),
            store.getters['lost/cruBenefits/all'],
            state,
            store.getters['lost/interimPayments/interimPayments'],
            store.getters['lost/lossOfIncome/pastLossTotal'],
        ).then(data => dispatch('updateInterestValues', data));
    },

    /**
     * Clear all the interest to 0
     *
     * @param commit
     * @param state
     */
    clearInterest({commit, state}) {
        state.customHeadsOfLoss.forEach((item, key) => {
            commit(mutationTypes.UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE, {key: 'interestAccrued', value: 0, index: key});
        });

        commit(mutationTypes.CLEAR_INTEREST);
    },

    /**
     * Update the interest values
     *
     * @param commit
     * @param dispatch
     * @param data
     */
    updateInterestValues({commit, dispatch}, data) {
        Object.keys(data).forEach((key) => {
            if (key.includes("custom_") ) {
                commit(mutationTypes.UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE, {
                    key: data[key]['key'],
                    value: data[key]['value'],
                    index: data[key]['index']
                });
            }
            commit(mutationTypes.UPDATE_INTEREST_VALUE, {key: key+'WithInterest', value: data[key]});
        });
    },

    /**
     * Calculate the custom interest
     *
     * @param dispatch
     * @param state
     */
    calcCustomInterest: _.debounce(({ dispatch, state }) => {
        let assumedSettlement = store.getters['lost/claimant/getClaimantInfoValue']('dateOfAssumedSettlement');
        let onsetOfSymptoms = store.getters['lost/claimant/getClaimantValue']('onsetOfSymptoms');

        return Api.calculators.monetary.customInterest(assumedSettlement, onsetOfSymptoms, state)
            .then(data => dispatch('updateCustomHeadsOfLossInterestValue', data))
            .then(() => dispatch('forceSave'))
    }, 500),

    /**
     * Calculate the custom interest
     *
     * @param commit
     * @param dispatch
     * @param data
     */
    updateCustomHeadsOfLossInterestValue({commit, dispatch}, data) {
        data.map((obj, index) => {
            Object.keys(obj).forEach((key) => {
                const value = obj[key];
                commit(mutationTypes.UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE, { key, value, index });
            });
        });
    },

    /**
     * Force a save after all interest is calculated
     *
     * @param dispatch
     */
    forceSave({ dispatch }) {
       let payload = {
            claim_type: 'lost_years',
            claim_id: store.getters['claim/id'],
            step_number: 4,
            data: {}
        };

        dispatch('save', payload);
    },

    proceduralStatusChanges({commit}, status) {
        if (status==="litigated") {
            commit(mutationTypes.UPDATE_INTEREST_VALUE,{key:'generalDamagesInterest', value: 2});
        } else {
            commit(mutationTypes.UPDATE_INTEREST_VALUE,{key:'generalDamagesInterest', value: 0});
        }
    }
};

// Mutations
const mutations = {
    /**
     * Update heads of loss data in the state.
     */
    [mutationTypes.UPDATE_HEADS_OF_LOSS_VALUE](state, payload) {
        state[payload.key] = payload.value;
    },
    /**
     * Update heads of loss data in the state.
     */
    [mutationTypes.UPDATE_INTEREST_VALUE](state, {key, value}) {
        state[key] = numberVal(value);
    },
    /**
     * Update care data in the state.
     */
    [mutationTypes.UPDATE_CARE_VALUE](state, payload) {
        if (!payload.type) {
            state.care[payload.key] = payload.value;
            return;
        }
        state.care[payload.type][payload.key] = payload.value;
    },
    /**
     * Add custom heads of loss data in the state.
     */
    [mutationTypes.ADD_CUSTOM_HEAD_OF_LOSS](state) {
        state.customHeadsOfLoss.push(customHeadsOfLossTemplate());
    },
    /**
     * Update custom heads of loss data in the state.
     */
    [mutationTypes.UPDATE_CUSTOM_HEAD_OF_LOSS_VALUE](state, {key, value, index}) {
        state.customHeadsOfLoss[index][key] = value;
    },
    /**
     * Delete custom heads of loss data in the state.
     */
    [mutationTypes.DELETE_CUSTOM_HEAD_OF_LOSS](state, index) {
        if (index === 0 && state.customHeadsOfLoss.length === 1) {
            state.customHeadsOfLoss = [];
        }
        state.customHeadsOfLoss.splice(index, 1);
    },
    /**
     * Add care period data in the state.
     */
    [mutationTypes.ADD_CARE_PERIOD](state, type) {
        state.care[type].periods.push(carePeriodTemplate());
    },
    /**
     * Delete care period in the state.
     */
    [mutationTypes.DELETE_CARE_PERIOD](state, {index, type}) {
        state.care[type].periods.splice(index, 1);
    },
    /**
     * Update care period in the state.
     */
    [mutationTypes.UPDATE_CARE_PERIOD_VALUE](state, { key, value, index, type }) {
        state.care[type].periods[index][key] = value;
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET](state, payload) {
        // Might be able to do this nicer with Object.assign
        Vue.set(state, payload.key, payload.value);
    },
    /**
     * Add data to the state from the server
     */
    [mutationTypes.UPDATE_FROM_SERVER](state, { data }) {
        state.generalDamages = data.generalDamages;
        state.pastCostOfTravel = data.pastCostOfTravel;
        state.pastLossOfEarnings = data.pastLossOfEarnings;
        state.pastDependencyOnServices = data.pastDependencyOnServices;
        state.futureDependencyOnServices = data.futureDependencyOnServices;
        state.pastCostOfCare = data.pastCostOfCare;
        state.futureCostOfCare = data.futureCostOfCare;
        state.misc = data.misc;
        state.generalDamagesWithInterest = data.generalDamagesWithInterest;
        state.pastCostOfTravelWithInterest = data.pastCostOfTravelWithInterest;
        state.pastCostOfCareWithInterest = data.pastCostOfCareWithInterest;
        state.futureCostOfCareWithInterest = data.futureCostOfCareWithInterest;
        state.pastDependencyOnServicesWithInterest = data.pastDependencyOnServicesWithInterest;
        state.miscWithInterest = data.miscWithInterest;
        state.pastLossOfEarningsWithInterest = data.pastLossOfEarningsWithInterest;
        state.generalDamagesInterest = data.generalDamagesInterest;
        state.pastCostOfTravelInterest = data.pastCostOfTravelInterest;
        state.miscInterest = data.miscInterest;
        state.pastCostOfCareInterest = data.pastCostOfCareInterest;
        state.futureCostOfCareInterest = data.futureCostOfCareInterest;
        state.pastLossOfEarningsInterest = data.pastLossOfEarningsInterest;
        state.customHeadsOfLoss = data.customHeadsOfLoss;
        state.care = data.care;
        state.interestType = data.interestType;
        state.customInterestDate = data.customInterestDate;
    },
    /**
     * Reset state.
     */
    [mutationTypes.RESET](state) {
        state.generalDamages = 0;
        state.pastCostOfTravel = 0;
        state.pastLossOfEarnings = 0;
        state.pastCostOfCare = 0;
        state.futureCostOfCare = 0;
        state.pastDependencyOnServices = 0;
        state.futureDependencyOnServices = 0;
        state.misc = 0;
        state.generalDamagesWithInterest = 0;
        state.pastCostOfTravelWithInterest = 0;
        state.pastCostOfCareWithInterest = 0;
        state.futureCostOfCareWithInterest = 0;
        state.pastDependencyOnServicesWithInterest = 0;
        state.miscWithInterest = 0;
        state.pastLossOfEarningsWithInterest = 0;
        state.generalDamagesInterest = 2.00;
        state.pastCostOfTravelInterest = 0.5;
        state.miscInterest = 0.5;
        state.pastCostOfCareInterest = 0.5;
        state.futureCostOfCareInterest = 0.5;
        state.pastLossOfEarningsInterest = 0.5;
        state.pastDependencyOnServicesInterest = 0.5;
        state.customHeadsOfLoss = [];
        state.care = careTemplate();
        state.interestType = 'today';
        state.customInterestDate = '';
    },
    /**
     * Reset interest values.
     */
    [mutationTypes.CLEAR_INTEREST](state) {
        state.generalDamagesWithInterest = 0;
        state.pastCostOfTravelWithInterest = 0;
        state.pastCostOfCareWithInterest = 0;
        state.futureCostOfCareWithInterest = 0;
        state.pastDependencyOnServicesWithInterest = 0;
        state.miscWithInterest = 0;
        state.pastLossOfEarningsWithInterest = 0;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/lost/headsOfLoss.js